










































































import Vue from "vue";
import Component from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import { Getter, Action, Mutation } from "vuex-class";
import VueI18n from "vue-i18n";
import Cookies from "js-cookie";
import { localize } from "vee-validate";
import {
    AppGetterTypes,
    AppActionTypes,
    SetPromotion,
    AppMutationTypes,
    SetCheckout,
    AddTracking
} from "../store";
import {
    IPromotionModel,
    ICheckoutModel,
    IEmailInformation,
    PageType
} from "../models";
import { IValidator } from "../validation";
import {
    cookieKey,
    pushFacebookInit,
    pushPageView,
    pushInitiateCheckout,
    GtmPageType
} from "../tracking";
import {
    getPromotionRequestModel,
    getTrackingRequest,
    getVisitTrackingRequest
} from "../models/factories";
import {
    setCheckoutFromQueryParams,
    getPromotionProducts,
    getOrderTotal
} from "../helpers";

@Component({ name: "CheckoutStep1" })
export default class CheckoutStep1 extends Vue {
    /**
     * Vuetype doesn't recognize the reference to `$i18n` on `$root`.
     * All this is doing is defining that `$root` will be a Vue instance with the property `$i18n` (VueI18n)
     */
    $root!: Vue & { $i18n: VueI18n };

    $refs!: {
        validationObserver: IValidator;
    };

    @Getter(AppGetterTypes.promotion)
    promotion!: IPromotionModel;

    // this is mapped from :slug in the route
    @Prop({ required: true })
    promotionSlug!: string;

    @Action(AppActionTypes.getPromotion)
    getPromotion!: SetPromotion;

    @Action(AppActionTypes.addTracking)
    addTracking!: AddTracking;

    @Mutation(AppMutationTypes.setCheckout)
    setCheckout!: SetCheckout;

    @Getter(AppGetterTypes.checkout)
    checkout!: ICheckoutModel;
    isLoading = true;

    get invalidCheckbox(): boolean {
        return !!this.promotion.emailOptInRequired && !this.checkout.emailOptIn;
    }

    async mounted(): Promise<void> {
        const params = getPromotionRequestModel(
            this.$route.query,
            this.promotionSlug
        );

        await this.getPromotion(params);

        this.isLoading = false;

        // Push GTM events
        if (this.promotion.facebookPixelId) {
            await pushFacebookInit(this.promotion.facebookPixelId);
        }

        if (this.promotion.sessionId) {
            let leadSku = "";
            let promotionPrice = 0;

            if (this.promotion.products) {
                //Get ExternalId/SKU from first promotion item
                leadSku =
                    getPromotionProducts(this.promotion.products)[0]
                        .externalId ?? "";

                //Calculate total price w/ shipping for all promotion items
                promotionPrice = getOrderTotal(this.promotion.products);
            }

            if (this.promotion.promotionCode){
                await pushInitiateCheckout(
                    this.promotion.sessionId,
                    leadSku,
                    promotionPrice,
                    this.promotion.promotionCode
                );
            }
        }

        await pushPageView(GtmPageType.Cart);
    }

    @Watch("promotion")
    async onPromotionChange(): Promise<void> {
        if (!this.promotion) {
            return;
        }

        // Set locale
        if (this.promotion.locale) {
            this.setLocale(this.promotion.locale);
        }

        this.isLoading = false;

        // track the page visit - this is separate from the email tracking event so we log visits on the initial page load, before we have a valid email address
        await this.addTracking(
            getVisitTrackingRequest(this.promotion, PageType.EmailPage, window.location.href)
        );
    }

    setEmailCookie(): void {
        const emailInfo: IEmailInformation = {
            email: this.checkout.email || "",
            emailOptIn: !!this.checkout.emailOptIn
        };

        Cookies.set(cookieKey.espiredEmail, emailInfo);
    }

    @Watch("checkout")
    setCheckoutQueryParams(): void {
        if (!this.checkout) {
            return;
        }

        setCheckoutFromQueryParams(this.checkout, this.$route.query);
    }

    /**
     * Sets the locale of the site based on the current promotion.
     */
    setLocale(locale: string): void {
        if (!locale) {
            return;
        }

        // vue-i18n:
        this.$root.$i18n.locale = locale;
        // vee-validate:
        localize(locale);
    }

    async goNext(): Promise<void> {
        const valid = await this.$refs.validationObserver.validate();

        if (!valid) {
            return;
        }

        this.isLoading = true;

        if (this.checkout) {
            this.checkout.promotionCode = this.promotion.promotionCode;

            this.setCheckout(this.checkout);
            this.setEmailCookie();
        }

        // track the email step (step 1)
        if (this.promotion.sessionId) {
            this.addTracking(
                getTrackingRequest(
                    "email",
                    this.checkout,
                    this.promotion.sessionId,
                    window.location.href
                )
            );
        }

        await this.$router.push({
            name: "step-2",
            params: { slug: this.promotionSlug },
            query: this.$route.query // Pass all current query parameters to step 2
        });

        this.isLoading = false;
    }
}
